import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, FileText, Globe2, Image, Link2, ListTree, Mic, Newspaper, Settings } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import ContentDocEditor from "../../components/admin/ContentDocEditor";
import PageSectionsEditor from "../../components/admin/PageSectionsEditor";
import FieldEditor from "../../components/admin/FieldEditor";
import MediaLibrary from "../../components/admin/MediaLibrary";
import CrudManager from "../../components/admin/CrudManager";
import { PageHeader, StatusPill } from "../../components/admin/ui";
import { PAGE_PATHS } from "../pageRegistry";
import { humanize, shortDate } from "../../lib/format";

const TABS = [["pages", "Pages", FileText], ["media", "Media Library", Image], ["navigation", "Navigation", ListTree], ["settings", "Global Settings", Settings],
  ["speakers", "Speakers", Mic], ["news", "News & Updates", Newspaper]];
const LINKS = [["/admin/event", "Event Details & Venue"], ["/admin/event", "Registration Categories"], ["/admin/sponsors", "Sponsorship Packages"]];

function NavigationEditor({ draft, onChange }) {
  const items = draft.items;
  const put = (list) => onChange({ ...draft, items: list });
  const move = (i, d) => { const l = [...items]; [l[i], l[i + d]] = [l[i + d], l[i]]; put(l); };
  return (
    <ul className="space-y-2" data-testid="navigation-editor">
      {items.map((it, i) => (
        <li key={i} className="flex flex-wrap items-center gap-3 rounded border border-slate-200 bg-white p-3">
          <span className="flex flex-col"><button type="button" disabled={!i} onClick={() => move(i, -1)} aria-label={`Move ${it.label} up`}><ArrowUp size={14} /></button>
            <button type="button" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${it.label} down`}><ArrowDown size={14} /></button></span>
          <label className="sr-only" htmlFor={`nav-l-${i}`}>Label</label>
          <input id={`nav-l-${i}`} className="field-input w-40 py-1.5 text-sm" value={it.label} onChange={(e) => put(items.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))} data-testid={`nav-item-label-${i}`} />
          <span className="font-mono text-xs text-slate-600">{it.path}</span>
          <label className="ml-auto flex items-center gap-2 text-sm"><Switch checked={it.visible} onCheckedChange={(v) => put(items.map((x, k) => (k === i ? { ...x, visible: v } : x)))} data-testid={`nav-item-visible-${i}`} />Visible</label>
        </li>
      ))}
    </ul>
  );
}

function PagesTab() {
  const [slug, setSlug] = useState("home");
  return (
    <div className="grid gap-6 lg:grid-cols-[200px_1fr]">
      <ul className="space-y-1" aria-label="Pages">
        {Object.keys(PAGE_PATHS).map((s) => (
          <li key={s}><button type="button" onClick={() => setSlug(s)} aria-current={s === slug} data-testid={`cms-page-select-${s}`}
            className={`w-full rounded px-3 py-2 text-left text-sm ${s === slug ? "bg-paleblue font-semibold text-navy" : "hover:bg-slate-100"}`}>{humanize(s)}</button></li>
        ))}
      </ul>
      <ContentDocEditor key={slug} contentKey={`page:${slug}`} previewSlug={slug} livePath={PAGE_PATHS[slug]}>
        {(draft, update) => <PageSectionsEditor draft={draft} onChange={update} />}
      </ContentDocEditor>
    </div>
  );
}

const TAB_VIEWS = {
  pages: () => <PagesTab />,
  media: () => <MediaLibrary />,
  navigation: () => <ContentDocEditor contentKey="navigation" livePath="/">{(d, u) => <NavigationEditor draft={d} onChange={u} />}</ContentDocEditor>,
  settings: () => <ContentDocEditor contentKey="settings" livePath="/">{(d, u) => <FieldEditor idPrefix="settings" value={d} onChange={u} />}</ContentDocEditor>,
  speakers: () => (
    <CrudManager endpoint="/admin/speakers" title="Speakers" testid="speakers-crud" defaults={{ visible: true, order: 10 }}
      fields={[{ name: "name", label: "Name" }, { name: "post_nominals", label: "Post-nominals (e.g. JP, LLB (Hons), FCIArb)" }, { name: "title", label: "Card title (one line)" },
        { name: "subtitle", label: "Profile subtitle" }, { name: "organization", label: "Organization" }, { name: "bio", label: "Short intro", type: "textarea" },
        { name: "profile", label: "Full profile (headings + bullet lists)", type: "richtext" }, { name: "slug", label: "Link name (e.g. john-bassie → /speakers#john-bassie)" },
        { name: "photo_url", label: "Photo", type: "image" }, { name: "session", label: "Panel / session (e.g. Construction Adjudication)" },
        { name: "session_role", label: "Panel role", type: "select", options: ["", "Moderator", "Panellist"] }, { name: "visible", label: "Visible", type: "switch" }, { name: "order", label: "Order", type: "number" }]}
      columns={[{ key: "order", label: "#" }, { key: "name", label: "Name" }, { key: "title", label: "Role" }, { key: "v", label: "Status", render: (r) => <StatusPill value={r.visible ? "published" : "draft"} /> }]} />),
  news: () => (
    <CrudManager endpoint="/admin/news" title="News" testid="news-crud" defaults={{ status: "draft", body: "<p></p>" }}
      fields={[{ name: "title", label: "Title" }, { name: "slug", label: "Slug (optional)" }, { name: "excerpt", label: "Excerpt", type: "textarea" },
        { name: "body", label: "Body", type: "richtext" }, { name: "image_url", label: "Image", type: "image" },
        { name: "status", label: "Status", type: "select", options: ["draft", "published"], help: "Publishing requires publish permission." }]}
      columns={[{ key: "title", label: "Title" }, { key: "s", label: "Status", render: (r) => <StatusPill value={r.status} /> }, { key: "d", label: "Published", render: (r) => shortDate(r.published_at) }]} />),
};

export default function Content() {
  const [tab, setTab] = useState("pages");
  const View = TAB_VIEWS[tab];
  return (
    <div data-testid="admin-content">
      <PageHeader title="Website Content" subtitle="Save Draft never changes the public site. Publish Changes makes the draft live." />
      <div className="grid gap-6 xl:grid-cols-[220px_1fr]">
        <nav aria-label="Content sections" className="admin-card h-fit p-3">
          <ul className="space-y-1">
            {TABS.map(([k, l, Ico]) => (
              <li key={k}><button type="button" onClick={() => setTab(k)} aria-current={tab === k} data-testid={`cms-tab-${k}`}
                className={`flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm ${tab === k ? "bg-burgundy font-semibold text-white" : "text-navy hover:bg-slate-100"}`}><Ico size={16} aria-hidden="true" />{l}</button></li>
            ))}
          </ul>
          <p className="mb-1 mt-4 px-3 text-xs font-semibold uppercase text-slate-500">Event</p>
          <ul className="space-y-1">{LINKS.map(([to, l]) => <li key={l}><Link to={to} className="flex items-center gap-2 rounded px-3 py-2 text-sm text-navy hover:bg-slate-100"><Link2 size={16} aria-hidden="true" />{l}</Link></li>)}
            <li><button type="button" onClick={() => setTab("pages")} className="flex items-center gap-2 rounded px-3 py-2 text-sm text-navy hover:bg-slate-100"><Globe2 size={16} aria-hidden="true" />Venue page</button></li></ul>
        </nav>
        <div className="admin-card min-w-0"><View /></div>
      </div>
    </div>
  );
}
